import { Model, Factory, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { rand, randBoolean, randCompanyName, randUrl } from '@ngneat/falso'

import type { MockServer } from './server.js'
import { createErrorResponse } from './utils.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import {
  type CustomIntegrationAttributes,
  OBJECT_TYPE,
} from '@/composables/customIntegrations.js'

const customIntegrationTypes = [
  'CLIENT',
  'PATIENT',
  'INVOICE',
  'CONSULTATION',
  'LABORATORY_REFERRAL',
  'APPOINTMENTS_CALENDAR',
  'SHIFTS_CALENDAR',
  'DIAGNOSTIC_IMAGING',
  'REMINDER',
  'HEALTH_PLAN',
  'ONLINE_PRESCRIPTION_CLIENT',
  'ONLINE_PRESCRIPTION_PATIENT',
  'ONLINE_PRESCRIPTION_CONSULTATION',
  'HOME_DELIVERY',
] as const

const actionTypes = ['BACKGROUND', 'TAB', 'POPUP', 'PANEL'] as const

const methodTypes = ['GET', 'POST'] as const

export const model = Model as ModelDefinition<CustomIntegrationAttributes>

export const factory = Factory.extend<CustomIntegrationAttributes>({
  name() {
    return `${randCompanyName()} Integration`
  },
  url() {
    return randUrl()
  },
  enabled() {
    return randBoolean()
  },
  custom_integration_type() {
    return rand(customIntegrationTypes)
  },
  action() {
    return rand(actionTypes)
  },
  method() {
    return rand(methodTypes)
  },
  parameter_name() {
    return 'id'
  },
  headers() {
    return {}
  },
  verification() {
    return randBoolean()
  },
  verification_salt() {
    return ''
  },
  expose_organization_info() {
    return randBoolean()
  },
  created() {
    return getTodayDateTimeISO()
  },
  modified() {
    return getTodayDateTimeISO()
  },
})

export function routes(server: MockServer) {
  server.get('/custom_integrations', function (schema) {
    return schema.all('customIntegration')
  })

  server.get('/custom_integrations/:id', function (schema, request) {
    const id = request.params.id
    const customIntegration = schema.find('customIntegration', id)

    if (!customIntegration) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Custom integration with id ${id} not found` }],
      })
    }

    return customIntegration
  })

  server.get('/custom_integrations/for_object', function (schema, request) {
    const objectType = request.queryParams.object_type
    const objectId = request.queryParams.object_id

    // Convert numeric object_type to string integration type name
    const integrationType = Object.entries(OBJECT_TYPE).find(
      ([, value]) => value === Number(objectType),
    )?.[0]

    // Filter integrations by type and enabled status
    const filteredIntegrations = schema.where(
      'customIntegration',
      (integration: CustomIntegrationAttributes) =>
        integration.custom_integration_type === integrationType &&
        integration.enabled === true,
    )

    // Map to add computed fields without mutation
    const integrationsWithComputedFields = filteredIntegrations.models.map(
      (integration) => {
        const attrs = { ...integration.attrs }

        // Add computed parameter_value if needed
        if (attrs.parameter_name && objectId) {
          attrs.parameter_value = Number(objectId)
        }

        // Add verification fields if verification is enabled
        if (attrs.verification) {
          attrs.verification_timestamp = getTodayDateTimeISO()
          attrs.verification_hash = 'mock-verification-hash-' + integration.id
        }

        return {
          type: integration.modelName,
          id: integration.id,
          attributes: attrs,
        }
      },
    )

    return { data: integrationsWithComputedFields }
  })

  server.post('/custom_integrations/:id/trigger', function (schema, request) {
    const id = request.params.id
    const customIntegration = schema.find('customIntegration', id)

    if (!customIntegration) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Custom integration with id ${id} not found` }],
      })
    }

    return new Response(200)
  })
}

export function seeds(server: MockServer) {
  server.createList('customIntegration', 20)

  // Add test integrations for CONSULTATION type
  server.create('customIntegration', {
    name: 'TEST: Popup POST Integration (Server-Side)',
    url: '/api/test-integration-post',
    enabled: true,
    custom_integration_type: 'CONSULTATION',
    action: 'POPUP',
    method: 'POST',
  })

  server.create('customIntegration', {
    name: 'External Lab System',
    url: 'https://external-lab.example.com/api/consultation',
    enabled: true,
    custom_integration_type: 'CONSULTATION',
    action: 'PANEL',
    method: 'GET',
  })

  server.create('customIntegration', {
    name: 'Background Data Sync',
    url: 'https://sync-service.example.com/webhook',
    enabled: true,
    custom_integration_type: 'CONSULTATION',
    action: 'BACKGROUND',
    method: 'POST',
  })

  server.create('customIntegration', {
    name: 'Imaging Viewer (TAB)',
    url: 'https://imaging.example.com/viewer',
    enabled: true,
    custom_integration_type: 'CONSULTATION',
    action: 'TAB',
    method: 'GET',
  })

  server.create('customIntegration', {
    name: 'TEST: Tab POST Integration',
    url: '/api/test-integration-post',
    enabled: true,
    custom_integration_type: 'CONSULTATION',
    action: 'TAB',
    method: 'POST',
  })
}
